import { CACHE_PROFILES } from "@/config/query-cache"
import {
  transport,
  useTransportQuery,
  type TransportRequestError,
} from "@/transport"
import type { UseQueryResult } from "@tanstack/react-query"

import type { GetMetricVersionsResponse } from "@/types_api/backend/transport/get_metric_versions_response"

export type MetricVersionResource =
  GetMetricVersionsResponse["versions"][number]

interface UseMetricVersionsOptions {
  enabled?: boolean
}

type MetricVersionsResult = UseQueryResult<
  GetMetricVersionsResponse,
  TransportRequestError
> & {
  versions: MetricVersionResource[]
}

/**
 * Newest-first prior-version history for a metric. The live metric row is the
 * current version and is not part of this list (copy-on-save model), so callers
 * render it separately. Pass `enabled: false` to defer the fetch until needed
 * (e.g. only when the version-history view opens).
 */
export function useMetricVersionsTransport(
  metricId: string | null,
  options?: UseMetricVersionsOptions,
): MetricVersionsResult {
  const resolvedId = metricId ?? ""
  const enabled = (options?.enabled ?? true) && resolvedId !== ""
  const query = useTransportQuery({
    operation: transport.operations.getMetricVersions,
    request: { metric_id: resolvedId },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
    },
  })

  return {
    ...query,
    versions: query.data?.versions ?? [],
  }
}
